import React, { useState, useEffect } from "react";
import {
  Map,
  BarChart3,
  Eye,
  FileText,
  RefreshCw,
  TrendingUp,
  Globe,
  LayoutDashboard,
  TrendingDown,
  TrendingUp as TrendingUpIcon,
  ChevronRight,
  Pencil,
  Search,
  X,
} from "lucide-react";
import { theme } from "../../styles/theme";
import scenarioApi, {
  ScenarioDefinition,
  MetricScenario,
} from "../../api/scenarioApi";
import administrationApi, { Country } from "../../api/administrationApi";
import TemporalDistributions from "./TemporalDistributions";
import DefaultTemporalDistributions from "./DefaultTemporalDistributions";
import { LensManagement } from "./Lenses";
import GlobalImpactMap from "./GlobalImpactMap";
import { MetricsManagement } from "../metrics";
import ImpactDashboard from "./ImpactDashboard";

interface ImpactClustersProps {
  darkMode: boolean;
}

interface GroupedMetricScenarios {
  clusterName: string;
  metricsCount: number;
  scenarios: MetricScenario[];
}

// Component to display metric scenarios in a table grouped by cluster
const MetricScenariosTable: React.FC<{
  metricScenarios: MetricScenario[];
  darkMode: boolean;
}> = ({ metricScenarios, darkMode }) => {
  // State for expanded/collapsed clusters
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(
    new Set(),
  );
  const [editingMetric, setEditingMetric] = useState<{
    metricId: string;
    scenarioType: string;
  } | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  // Helper functions defined before useMemo
  const formatPercentage = (
    value?: number | null | { Valid: boolean; Float64: number },
  ): string => {
    // Handle SQL null types from backend
    if (
      value &&
      typeof value === "object" &&
      "Valid" in value &&
      "Float64" in value
    ) {
      if (!value.Valid) return "0.00%";
      return `${value.Float64.toFixed(2)}%`;
    }
    if (value === undefined || value === null) return "0.00%";
    // Ensure value is a number
    const numValue =
      typeof value === "number" ? value : parseFloat(String(value));
    if (isNaN(numValue)) return "0.00%";
    return `${numValue.toFixed(2)}%`;
  };

  // Natural sort function for metric IDs (C1.1, C1.2, C1.10, C1.11, etc.)
  const naturalSortMetricId = (a: string, b: string): number => {
    const parseMetricId = (id: string) => {
      const match = id.match(/^C(\d+)\.(\d+)$/);
      if (!match) return { cluster: 0, metric: 0 };
      return { cluster: parseInt(match[1]), metric: parseInt(match[2]) };
    };

    const aParsed = parseMetricId(a);
    const bParsed = parseMetricId(b);

    if (aParsed.cluster !== bParsed.cluster) {
      return aParsed.cluster - bParsed.cluster;
    }
    return aParsed.metric - bParsed.metric;
  };

  // Toggle cluster expansion
  const toggleCluster = (clusterCode: string) => {
    const newExpanded = new Set(expandedClusters);
    if (newExpanded.has(clusterCode)) {
      newExpanded.delete(clusterCode);
    } else {
      newExpanded.add(clusterCode);
    }
    setExpandedClusters(newExpanded);
  };

  // Group scenarios by cluster using actual cluster data from database
  const groupedScenarios = React.useMemo(() => {
    const groups: {
      [key: string]: GroupedMetricScenarios & { clusterCode: string };
    } = {};

    metricScenarios.forEach((scenario) => {
      const clusterCode = scenario.cluster_code || "Other";

      if (!groups[clusterCode]) {
        groups[clusterCode] = {
          clusterCode: clusterCode,
          clusterName: scenario.cluster_name || clusterCode,
          metricsCount: 0,
          scenarios: [],
        };
      }

      groups[clusterCode].scenarios.push(scenario);
    });

    // Count unique metrics per cluster
    Object.keys(groups).forEach((cluster) => {
      const uniqueMetrics = new Set(
        groups[cluster].scenarios.map((s) => s.metric_id),
      );
      groups[cluster].metricsCount = uniqueMetrics.size;
    });

    // Sort clusters by cluster code (C1, C2, C3, etc.)
    return Object.values(groups).sort((a, b) => {
      const aNum = parseInt(a.clusterCode.replace("C", "")) || 999;
      const bNum = parseInt(b.clusterCode.replace("C", "")) || 999;
      return aNum - bNum;
    });
  }, [metricScenarios]);

  // Clusters are initially collapsed

  return (
    <div className="space-y-6">
      {groupedScenarios.map((group) => (
        <div key={group.clusterCode}>
          {/* Cluster Header - Clickable for expand/collapse */}
          <div
            className="flex items-center justify-between mb-3 py-2 border-b cursor-pointer hover:bg-black hover:bg-opacity-5 transition-colors rounded px-2"
            style={{
              borderColor: darkMode
                ? theme.colors.border.dark
                : theme.colors.border.light,
            }}
            onClick={() => toggleCluster(group.clusterCode)}
          >
            <div className="flex items-center space-x-2">
              {/* Chevron icon for expand/collapse */}
              <ChevronRight
                className={`w-4 h-4 transition-transform ${expandedClusters.has(group.clusterCode) ? "rotate-90" : ""}`}
                style={{ color: theme.colors.text.secondary }}
              />
              <h4
                className="font-medium"
                style={{
                  color: darkMode
                    ? theme.colors.text.darkPrimary
                    : theme.colors.text.primary,
                }}
              >
                {group.clusterCode} - {group.clusterName}
              </h4>
            </div>
            <span
              className="text-sm"
              style={{ color: theme.colors.text.secondary }}
            >
              {group.metricsCount} metrics
            </span>
          </div>

          {/* Metrics Table - Only show if expanded */}
          {expandedClusters.has(group.clusterCode) && (
            <div className="overflow-x-auto ml-6">
              <table className="w-full">
                <thead>
                  <tr
                    className="text-left text-sm"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    <th className="pb-2">Metric</th>
                    <th className="pb-2 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <TrendingDown
                          className="w-3.5 h-3.5"
                          style={{ color: theme.colors.status.error }}
                        />
                        <span>Pessimistic (%)</span>
                      </div>
                    </th>
                    <th className="pb-2 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <TrendingUp
                          className="w-3.5 h-3.5"
                          style={{ color: theme.colors.status.warning }}
                        />
                        <span>Baseline (%)</span>
                      </div>
                    </th>
                    <th className="pb-2 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <TrendingUpIcon
                          className="w-3.5 h-3.5"
                          style={{ color: theme.colors.status.success }}
                        />
                        <span>Optimistic (%)</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group scenarios by metric_id
                    const metricGroups: {
                      [key: string]: { [key: string]: MetricScenario };
                    } = {};
                    group.scenarios.forEach((scenario) => {
                      if (!metricGroups[scenario.metric_id]) {
                        metricGroups[scenario.metric_id] = {};
                      }
                      metricGroups[scenario.metric_id][scenario.scenario_type] =
                        scenario;
                    });

                    // Sort metric IDs naturally (C1.1, C1.2, C1.10, etc.)
                    return Object.entries(metricGroups)
                      .sort(([a], [b]) => naturalSortMetricId(a, b))
                      .map(([metricId, scenarios]) => {
                        const isEditing = (type: string) =>
                          editingMetric?.metricId === metricId &&
                          editingMetric?.scenarioType === type;

                        const handleEdit = (
                          type: string,
                          currentValue: any,
                        ) => {
                          const value = formatPercentage(currentValue).replace(
                            "%",
                            "",
                          );
                          setEditingMetric({ metricId, scenarioType: type });
                          setEditValue(value);
                        };

                        const handleSave = async (type: string) => {
                          try {
                            const scenario = scenarios[type];
                            if (scenario) {
                              const percentageValue = parseFloat(editValue);
                              if (!isNaN(percentageValue)) {
                                await scenarioApi.updateMetricScenario(scenario.id, {
                                  percentage_change: percentageValue,
                                });
                                // Update the local state directly without full refresh
                                scenario.percentage_change = percentageValue;
                              }
                            }
                          } catch (error) {
                            console.error(
                              `Error saving ${type} for ${metricId}:`,
                              error,
                            );
                          }
                          setEditingMetric(null);
                          setEditValue("");
                        };

                        const handleCancel = () => {
                          setEditingMetric(null);
                          setEditValue("");
                        };

                        return (
                          <tr
                            key={metricId}
                            className="border-t"
                            style={{
                              borderColor: darkMode
                                ? theme.colors.border.dark
                                : theme.colors.border.light,
                            }}
                          >
                            <td className="py-3">
                              <div>
                                <p
                                  className="font-medium text-sm"
                                  style={{
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                >
                                  {metricId}
                                </p>
                                <p
                                  className="text-xs mt-0.5"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {scenarios["baseline"]?.metric_name ||
                                    scenarios["pessimistic"]?.metric_name ||
                                    scenarios["optimistic"]?.metric_name ||
                                    ""}
                                </p>
                              </div>
                            </td>
                            <td className="py-3 text-center">
                              {isEditing("pessimistic") ? (
                                <input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleSave("pessimistic")}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter")
                                      handleSave("pessimistic");
                                    if (e.key === "Escape") handleCancel();
                                  }}
                                  className="w-20 px-1 py-0.5 text-sm text-center rounded border"
                                  style={{
                                    backgroundColor: darkMode
                                      ? theme.colors.background.darkPaper
                                      : theme.colors.background.paper,
                                    borderColor: darkMode
                                      ? theme.colors.border.dark
                                      : theme.colors.border.light,
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span
                                  className="text-sm cursor-pointer hover:text-blue-500"
                                  style={{
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                  onClick={() =>
                                    handleEdit(
                                      "pessimistic",
                                      scenarios["pessimistic"]
                                        ?.percentage_change,
                                    )
                                  }
                                >
                                  {formatPercentage(
                                    scenarios["pessimistic"]?.percentage_change,
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="py-3 text-center">
                              {isEditing("baseline") ? (
                                <input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleSave("baseline")}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter")
                                      handleSave("baseline");
                                    if (e.key === "Escape") handleCancel();
                                  }}
                                  className="w-20 px-1 py-0.5 text-sm text-center rounded border"
                                  style={{
                                    backgroundColor: darkMode
                                      ? theme.colors.background.darkPaper
                                      : theme.colors.background.paper,
                                    borderColor: darkMode
                                      ? theme.colors.border.dark
                                      : theme.colors.border.light,
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span
                                  className="text-sm cursor-pointer hover:text-blue-500"
                                  style={{
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                  onClick={() =>
                                    handleEdit(
                                      "baseline",
                                      scenarios["baseline"]?.percentage_change,
                                    )
                                  }
                                >
                                  {formatPercentage(
                                    scenarios["baseline"]?.percentage_change,
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="py-3 text-center">
                              {isEditing("optimistic") ? (
                                <input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleSave("optimistic")}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter")
                                      handleSave("optimistic");
                                    if (e.key === "Escape") handleCancel();
                                  }}
                                  className="w-20 px-1 py-0.5 text-sm text-center rounded border"
                                  style={{
                                    backgroundColor: darkMode
                                      ? theme.colors.background.darkPaper
                                      : theme.colors.background.paper,
                                    borderColor: darkMode
                                      ? theme.colors.border.dark
                                      : theme.colors.border.light,
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <span
                                  className="text-sm cursor-pointer hover:text-blue-500"
                                  style={{
                                    color: darkMode
                                      ? theme.colors.text.darkPrimary
                                      : theme.colors.text.primary,
                                  }}
                                  onClick={() =>
                                    handleEdit(
                                      "optimistic",
                                      scenarios["optimistic"]
                                        ?.percentage_change,
                                    )
                                  }
                                >
                                  {formatPercentage(
                                    scenarios["optimistic"]?.percentage_change,
                                  )}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {metricScenarios.length === 0 && (
        <div
          className="text-center py-8"
          style={{ color: theme.colors.text.secondary }}
        >
          <p>No metric scenarios found for the selected country.</p>
        </div>
      )}
    </div>
  );
};

// Country Selector Component with Search and Multi-column Layout
interface CountrySelectorProps {
  countries: Country[];
  selectedCountry: number | null;
  onSelectCountry: (countryId: number | null) => void;
  darkMode: boolean;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  countries,
  selectedCountry,
  onSelectCountry,
  darkMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCountry, setHoveredCountry] = useState<number | null>(null);

  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCountryData = countries.find((c) => c.id === selectedCountry);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".country-selector")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="country-selector relative">
      {/* Selected Country Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-lg flex items-center justify-between"
        style={{
          backgroundColor: darkMode
            ? theme.colors.background.dark
            : theme.colors.background.default,
          border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
          color: darkMode
            ? theme.colors.text.darkPrimary
            : theme.colors.text.primary,
        }}
      >
        <div className="flex items-center space-x-2">
          {selectedCountryData ? (
            <>
              <span className="text-xl">{selectedCountryData.flag_emoji}</span>
              <span>{selectedCountryData.name}</span>
            </>
          ) : (
            <span style={{ color: theme.colors.text.secondary }}>
              Select a country...
            </span>
          )}
        </div>
        <ChevronRight
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
          style={{ color: theme.colors.text.secondary }}
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute z-50 w-full rounded-lg shadow-xl overflow-hidden"
          style={{
            backgroundColor: darkMode
              ? theme.colors.background.darkPaper
              : theme.colors.background.paper,
            border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
            maxWidth: "600px",
            minWidth: "400px",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "calc(100% + 8px)", // Position above the button
          }}
        >
          {/* Search Bar */}
          <div className="p-3 border-b" style={{
            borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
          }}>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: theme.colors.text.secondary }}
              />
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-lg"
                style={{
                  backgroundColor: darkMode
                    ? theme.colors.background.dark
                    : theme.colors.background.default,
                  border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  color: darkMode
                    ? theme.colors.text.darkPrimary
                    : theme.colors.text.primary,
                }}
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X
                    className="w-4 h-4"
                    style={{ color: theme.colors.text.secondary }}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Countries Grid */}
          <div
            className="p-3 grid grid-cols-2 gap-2 overflow-y-auto"
            style={{ maxHeight: "400px" }}
          >
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.id}
                  onClick={() => {
                    onSelectCountry(country.id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  onMouseEnter={() => setHoveredCountry(country.id)}
                  onMouseLeave={() => setHoveredCountry(null)}
                  className="flex items-center space-x-2 p-2 rounded-lg transition-all"
                  style={{
                    backgroundColor:
                      selectedCountry === country.id
                        ? theme.colors.primary.main + "20"
                        : hoveredCountry === country.id
                        ? darkMode
                          ? theme.colors.background.dark
                          : theme.colors.background.default
                        : "transparent",
                    border: `1px solid ${
                      selectedCountry === country.id
                        ? theme.colors.primary.main
                        : hoveredCountry === country.id
                        ? darkMode
                          ? theme.colors.border.dark
                          : theme.colors.border.light
                        : "transparent"
                    }`,
                    color:
                      selectedCountry === country.id
                        ? theme.colors.primary.main
                        : darkMode
                        ? theme.colors.text.darkPrimary
                        : theme.colors.text.primary,
                  }}
                >
                  <span className="text-xl">{country.flag_emoji}</span>
                  <span className="text-sm text-left flex-1">{country.name}</span>
                </button>
              ))
            ) : (
              <div
                className="col-span-2 text-center py-8"
                style={{ color: theme.colors.text.secondary }}
              >
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ImpactClusters: React.FC<ImpactClustersProps> = ({ darkMode }) => {
  const [activeTab, setActiveTab] = useState("global");
  const [scenarioDefinitions, setScenarioDefinitions] = useState<
    ScenarioDefinition[]
  >([]);
  const [editingScenario, setEditingScenario] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [metricScenarios, setMetricScenarios] = useState<MetricScenario[]>([]);
  const [scenarioSubTab, setScenarioSubTab] = useState<"static" | "temporal" | "defaults">(
    "static",
  );
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    if (activeTab === "scenarios") {
      fetchScenarioDefinitions();
      fetchCountries();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedCountry) {
      fetchMetricScenarios(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchScenarioDefinitions = async () => {
    try {
      const definitions = await scenarioApi.getScenarioDefinitions();
      setScenarioDefinitions(definitions);
    } catch (error) {
      console.error("Failed to fetch scenario definitions:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await administrationApi.getCountries({ limit: 1000 });
      setCountries(response.countries);
    } catch (error) {
      console.error("Failed to fetch countries:", error);
    }
  };

  const fetchMetricScenarios = async (countryId: number) => {
    setLoadingMetrics(true);
    try {
      const scenarios =
        await scenarioApi.getMetricScenariosByCountry(countryId);
      setMetricScenarios(scenarios);
    } catch (error) {
      console.error("Failed to fetch metric scenarios:", error);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleUpdateScenario = async (id: number) => {
    try {
      const value = parseFloat(editValue);
      if (!isNaN(value)) {
        await scenarioApi.updateScenarioDefinition(id, {
          default_percentage_adjustment: value,
        });
        await fetchScenarioDefinitions();
      }
      setEditingScenario(null);
      setEditValue("");
    } catch (error) {
      console.error("Failed to update scenario:", error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getScenarioIcon = (type: string) => {
    switch (type) {
      case "pessimistic":
        return TrendingDown;
      case "optimistic":
        return TrendingUpIcon;
      default:
        return TrendingUp;
    }
  };

  const getScenarioColor = (type: string) => {
    switch (type) {
      case "pessimistic":
        return theme.colors.status.error;
      case "optimistic":
        return theme.colors.status.success;
      default:
        return theme.colors.status.warning;
    }
  };

  // Removed unused metrics array - metrics are now managed through MetricsManagement component


  const tabs = [
    { id: "global", label: "Global", icon: Globe },
    { id: "dashboards", label: "Dashboards", icon: LayoutDashboard },
    { id: "metrics", label: "Metrics", icon: BarChart3 },
    { id: "lenses", label: "Lenses", icon: Eye },
    { id: "scenarios", label: "Scenarios", icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-bold flex items-center gap-2"
            style={{
              color: darkMode
                ? theme.colors.text.darkPrimary
                : theme.colors.text.primary,
            }}
          >
            <Map
              className="w-6 h-6"
              style={{ color: theme.colors.primary.main }}
            />
            Unify
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: theme.colors.text.secondary }}
          >
            Analyze and manage impact across different geographical and
            demographic clusters
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="flex space-x-1 p-1 rounded-lg"
        style={{
          backgroundColor: darkMode
            ? theme.colors.background.dark
            : theme.colors.background.default,
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
              style={{
                backgroundColor:
                  activeTab === tab.id
                    ? darkMode
                      ? theme.colors.background.darkPaper
                      : theme.colors.background.paper
                    : "transparent",
                color:
                  activeTab === tab.id
                    ? theme.colors.primary.main
                    : darkMode
                      ? theme.colors.text.darkSecondary
                      : theme.colors.text.secondary,
                boxShadow:
                  activeTab === tab.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div
        className="rounded-xl p-6"
        style={{
          backgroundColor: darkMode
            ? theme.colors.background.darkPaper
            : theme.colors.background.paper,
          boxShadow: theme.widget.boxShadow,
        }}
      >
        {activeTab === "global" && (
          <GlobalImpactMap darkMode={darkMode} />
        )}

        {activeTab === "dashboards" && (
          <ImpactDashboard darkMode={darkMode} />
        )}

        {activeTab === "metrics" && (
          <MetricsManagement darkMode={darkMode} />
        )}

        {activeTab === "lenses" && (
          <LensManagement darkMode={darkMode} />
        )}

        {activeTab === "scenarios" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2
                className="text-lg font-semibold"
                style={{
                  color: darkMode
                    ? theme.colors.text.darkPrimary
                    : theme.colors.text.primary,
                }}
              >
                Default Impact Scenarios
              </h2>
            </div>

            {/* Scenario Definitions Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {scenarioDefinitions.map((scenario) => {
                const Icon = getScenarioIcon(scenario.type);
                const color = getScenarioColor(scenario.type);

                return (
                  <div
                    key={scenario.id}
                    className="p-5 rounded-xl transition-all"
                    style={{
                      backgroundColor: darkMode
                        ? theme.colors.background.dark
                        : theme.colors.background.default,
                      border: `2px solid ${color}30`,
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Icon className="w-5 h-5" style={{ color }} />
                        <h3
                          className="font-semibold text-base"
                          style={{
                            color: darkMode
                              ? theme.colors.text.darkPrimary
                              : theme.colors.text.primary,
                          }}
                        >
                          {scenario.name}
                        </h3>
                      </div>
                    </div>

                    <p
                      className="text-sm mb-4"
                      style={{
                        color: theme.colors.text.secondary,
                        lineHeight: "1.5",
                      }}
                    >
                      {scenario.description ||
                        `${scenario.type.charAt(0).toUpperCase() + scenario.type.slice(1)} scenario with standard assumptions`}
                    </p>

                    <div className="space-y-3">
                      <div>
                        <p
                          className="text-xs mb-1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Default Adjustment
                        </p>
                        {editingScenario === scenario.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => handleUpdateScenario(scenario.id)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleUpdateScenario(scenario.id);
                                }
                              }}
                              className="px-2 py-1 rounded border text-sm w-20"
                              style={{
                                backgroundColor: darkMode
                                  ? theme.colors.background.darkPaper
                                  : theme.colors.background.paper,
                                borderColor: darkMode
                                  ? theme.colors.border.dark
                                  : theme.colors.border.light,
                                color: darkMode
                                  ? theme.colors.text.darkPrimary
                                  : theme.colors.text.primary,
                              }}
                              autoFocus
                            />
                            <span
                              className="text-sm"
                              style={{ color: theme.colors.text.secondary }}
                            >
                              %
                            </span>
                          </div>
                        ) : (
                          <p
                            className="text-lg font-semibold"
                            style={{
                              color: darkMode
                                ? theme.colors.text.darkPrimary
                                : theme.colors.text.primary,
                            }}
                          >
                            {scenario.default_percentage_adjustment ?? 0}%
                          </p>
                        )}
                      </div>

                      <div>
                        <p
                          className="text-xs"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Last Updated: {formatDate(scenario.updated_at)}
                        </p>
                      </div>
                    </div>

                    <div
                      className="mt-4 pt-3 border-t"
                      style={{
                        borderColor: darkMode
                          ? theme.colors.border.dark
                          : theme.colors.border.light,
                      }}
                    >
                      <button
                        onClick={() => {
                          setEditingScenario(scenario.id);
                          setEditValue(
                            String(scenario.default_percentage_adjustment ?? 0),
                          );
                        }}
                        className="flex items-center space-x-1 text-sm transition-colors hover:opacity-80"
                        style={{ color }}
                      >
                        <Pencil className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Default Temporal Distribution Section */}
            <div
              className="border-t pt-6 mb-6"
              style={{
                borderColor: darkMode
                  ? theme.colors.border.dark
                  : theme.colors.border.light,
              }}
            >
              <div className="mb-4">
                <h3
                  className="text-base font-medium"
                  style={{
                    color: darkMode
                      ? theme.colors.text.darkPrimary
                      : theme.colors.text.primary,
                  }}
                >
                  Default Temporal Distribution
                </h3>
                <p
                  className="text-sm mt-1"
                  style={{
                    color: theme.colors.text.secondary,
                  }}
                >
                  This distribution applies to all scenarios and countries when no custom distribution is defined
                </p>
              </div>
              <DefaultTemporalDistributions 
                darkMode={darkMode} 
                isAdmin={true} // TODO: Get actual admin status from user context
              />
            </div>

            {/* Existing Custom Scenarios Section */}
            <div
              className="border-t pt-6"
              style={{
                borderColor: darkMode
                  ? theme.colors.border.dark
                  : theme.colors.border.light,
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-base font-medium"
                  style={{
                    color: darkMode
                      ? theme.colors.text.darkPrimary
                      : theme.colors.text.primary,
                  }}
                >
                  Existing Custom Scenarios
                </h3>
              </div>

              {/* Country Selector Dropdown */}
              <div className="mb-4 relative">
                <CountrySelector
                  countries={countries}
                  selectedCountry={selectedCountry}
                  onSelectCountry={setSelectedCountry}
                  darkMode={darkMode}
                />
              </div>

              {/* Spatial and Temporal Tabs - Only show when country is selected */}
              {selectedCountry && (
                <>
                  <div className="flex space-x-1 mb-4">
                    <button
                      onClick={() => setScenarioSubTab("static")}
                      className="px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                      style={{
                        backgroundColor:
                          scenarioSubTab === "static"
                            ? theme.colors.primary.main
                            : "transparent",
                        color:
                          scenarioSubTab === "static"
                            ? "white"
                            : darkMode
                              ? theme.colors.text.darkSecondary
                              : theme.colors.text.secondary,
                        border: `1px solid ${
                          scenarioSubTab === "static"
                            ? theme.colors.primary.main
                            : darkMode
                              ? theme.colors.border.dark
                              : theme.colors.border.light
                        }`,
                      }}
                    >
                      Static
                    </button>
                    <button
                      onClick={() => setScenarioSubTab("temporal")}
                      className="px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                      style={{
                        backgroundColor:
                          scenarioSubTab === "temporal"
                            ? theme.colors.primary.main
                            : "transparent",
                        color:
                          scenarioSubTab === "temporal"
                            ? "white"
                            : darkMode
                              ? theme.colors.text.darkSecondary
                              : theme.colors.text.secondary,
                        border: `1px solid ${
                          scenarioSubTab === "temporal"
                            ? theme.colors.primary.main
                            : darkMode
                              ? theme.colors.border.dark
                              : theme.colors.border.light
                        }`,
                      }}
                    >
                      Temporal
                    </button>
                  </div>

                  {/* Tab Content */}
                  {scenarioSubTab === "static" && (
                    <div className="space-y-4">
                      {loadingMetrics ? (
                        <div className="text-center py-8">
                          <RefreshCw
                            className="w-8 h-8 animate-spin mx-auto"
                            style={{ color: theme.colors.primary.main }}
                          />
                          <p
                            className="mt-2 text-sm"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            Loading metrics...
                          </p>
                        </div>
                      ) : (
                        <MetricScenariosTable
                          metricScenarios={metricScenarios}
                          darkMode={darkMode}
                        />
                      )}
                    </div>
                  )}

                  {scenarioSubTab === "temporal" && (
                    <TemporalDistributions 
                      darkMode={darkMode} 
                      countries={countries}
                      selectedCountry={selectedCountry}
                      onCountryChange={setSelectedCountry}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpactClusters;
